import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

const PAGE_TOP = 0;
const SCROLL_KEY_PREFIX = "scrollPos:";
const SECTION_KEY_PREFIX = "scrollSection:";
// Keep retrying the restore while async content (product sections) grows the page.
const RESTORE_MAX_MS = 4000;
// Consider the layout done once the page height hasn't changed for this long.
const RESTORE_SETTLE_MS = 250;
// A section counts as "current" once its top passes this distance below the viewport top.
const SECTION_ACTIVE_OFFSET = 120;

const resetNativeScroll = () => {
    window.scrollTo({ top: PAGE_TOP, left: PAGE_TOP, behavior: "auto" });
    document.documentElement.scrollTop = PAGE_TOP;
    document.body.scrollTop = PAGE_TOP;
};

const getNavigationType = (): string => {
    const entries = performance.getEntriesByType("navigation");
    if (entries.length) {
        return (entries[0] as PerformanceNavigationTiming).type;
    }
    const legacy = (performance as unknown as { navigation?: { type: number } }).navigation;
    return legacy?.type === 1 ? "reload" : "navigate";
};

// The id of the anchored section whose top has most recently scrolled past
// the active offset — i.e. the section the user is currently viewing.
const getCurrentSectionId = (): string | null => {
    const anchors = document.querySelectorAll<HTMLElement>("[data-scroll-section]");
    let currentId: string | null = null;
    anchors.forEach((el) => {
        if (el.getBoundingClientRect().top <= SECTION_ACTIVE_OFFSET) {
            currentId = el.id || currentId;
        }
    });
    return currentId;
};

const getNativeScrollY = (): number =>
    window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || PAGE_TOP;

const persistRouteScroll = (route: string, scroll: number) => {
    try {
        sessionStorage.setItem(`${SCROLL_KEY_PREFIX}${route}`, String(Math.round(scroll)));
        const sectionId = getCurrentSectionId();
        if (sectionId) {
            sessionStorage.setItem(`${SECTION_KEY_PREFIX}${route}`, sectionId);
        } else {
            sessionStorage.removeItem(`${SECTION_KEY_PREFIX}${route}`);
        }
    } catch {
        // storage may be unavailable (private mode) - degrade silently
    }
};
export default function useLenis() {
    const rafRef = useRef<number | null>(null);
    const restoreRafRef = useRef<number | null>(null);
    const isRestoringRef = useRef(false);
    const lenisRef = useRef<Lenis | null>(null);
    const scrollPersistTimerRef = useRef<number | null>(null);
    const latestScrollRef = useRef(0);
    const isFirstRun = useRef(true);
    const { pathname, search } = useLocation();
    const previousPathnameRef = useRef(pathname);

    const routeKey = `${pathname}${search}`;
    const routeKeyRef = useRef(routeKey);
    routeKeyRef.current = routeKey;

    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.2,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            orientation: "vertical",
            gestureOrientation: "vertical",
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 2,
            infinite: false,
        });
        lenisRef.current = lenis;

        const persistScroll = ({ scroll }: { scroll: number }) => {
            // Scroll events can fire every animation frame. Keep the hot path
            // to one ref write and persist asynchronously at most 5 times/sec.
            if (isRestoringRef.current) return;
            latestScrollRef.current = scroll;
            if (scrollPersistTimerRef.current !== null) return;

            scrollPersistTimerRef.current = window.setTimeout(() => {
                scrollPersistTimerRef.current = null;
                persistRouteScroll(routeKeyRef.current, latestScrollRef.current);
            }, 200);
        };
        const persistBeforeUnload = () => {
            persistRouteScroll(routeKeyRef.current, getNativeScrollY());
        };
        lenis.on("scroll", persistScroll);
        window.addEventListener("pagehide", persistBeforeUnload);
        window.addEventListener("beforeunload", persistBeforeUnload);

        function raf(time: number) {
            lenis.raf(time);
            rafRef.current = requestAnimationFrame(raf);
        }
        rafRef.current = requestAnimationFrame(raf);

        return () => {
            lenis.off("scroll", persistScroll);
            window.removeEventListener("pagehide", persistBeforeUnload);
            window.removeEventListener("beforeunload", persistBeforeUnload);
            lenisRef.current = null;
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
            if (scrollPersistTimerRef.current !== null) {
                window.clearTimeout(scrollPersistTimerRef.current);
                scrollPersistTimerRef.current = null;
            }
            if (restoreRafRef.current != null) cancelAnimationFrame(restoreRafRef.current);
            try {
                lenis.destroy();
            } catch {
                // ignore destroy errors
            }
        };
    }, []);

    // Retry the restore each frame: async sections keep growing the page and
    // shifting anchor offsets, so keep re-aligning to the section until the
    // page height stays stable for a short window (or the user intervenes).
    const restoreScroll = (sectionId: string | null, fallbackY: number) => {
        const start = performance.now();
        let lastHeight = -1;
        let heightStableSince = start;
        let cancelled = false;
        isRestoringRef.current = true;

        const cancel = () => {
            cancelled = true;
        };
        // Any real user scroll input aborts the restore so we never fight them.
        window.addEventListener("wheel", cancel, { passive: true, once: true });
        window.addEventListener("touchstart", cancel, { passive: true, once: true });
        window.addEventListener("keydown", cancel, { once: true });

        const cleanup = () => {
            isRestoringRef.current = false;
            window.removeEventListener("wheel", cancel);
            window.removeEventListener("touchstart", cancel);
            window.removeEventListener("keydown", cancel);
        };

        const step = () => {
            if (cancelled) {
                cleanup();
                return;
            }

            const now = performance.now();
            const scrollHeight = document.documentElement.scrollHeight;
            if (scrollHeight !== lastHeight) {
                lastHeight = scrollHeight;
                heightStableSince = now;
            }

            const maxScroll = scrollHeight - window.innerHeight;
            const anchor = sectionId ? document.getElementById(sectionId) : null;
            const targetY = anchor
                ? window.scrollY + anchor.getBoundingClientRect().top
                : fallbackY;

            const y = Math.min(targetY, Math.max(PAGE_TOP, maxScroll));
            lenisRef.current?.scrollTo(y, { immediate: true });
            window.scrollTo(0, y);

            // Lazy/Suspense + async data means the target isn't reachable yet:
            // the saved section hasn't mounted, or the page can't scroll far
            // enough to reach it. Until then, ignore height-stability (the short
            // loading placeholder is "stable" too) and keep retrying.
            const contentReady = sectionId
                ? anchor != null && maxScroll >= targetY - 1
                : maxScroll >= fallbackY - 1;

            const heightSettled =
                contentReady && now - heightStableSince >= RESTORE_SETTLE_MS;
            const withinBudget = now - start < RESTORE_MAX_MS;
            if (!heightSettled && withinBudget) {
                restoreRafRef.current = requestAnimationFrame(step);
            } else {
                cleanup();
            }
        };
        restoreRafRef.current = requestAnimationFrame(step);
    };

    useEffect(() => {
        const wasFirstRun = isFirstRun.current;
        isFirstRun.current = false;
        const pathnameChanged = previousPathnameRef.current !== pathname;
        previousPathnameRef.current = pathname;

        // Filter and search parameter changes update the product list but are
        // not page navigation. Preserve the user's current scroll position.
        if (!wasFirstRun && !pathnameChanged) return;

        // On a genuine page reload, restore the section the user was viewing
        // instead of snapping to the top.
        if (wasFirstRun) {
            const navType = getNavigationType();
            const isRestorable = navType === "reload" || navType === "back_forward";
            const savedSection = sessionStorage.getItem(`${SECTION_KEY_PREFIX}${routeKey}`);
            const savedY = Number(sessionStorage.getItem(`${SCROLL_KEY_PREFIX}${routeKey}`) ?? "0");
            if (savedSection || (isRestorable && savedY > 0)) {
                restoreScroll(savedSection, savedY);
                return () => {
                    isRestoringRef.current = false;
                    if (restoreRafRef.current != null) {
                        cancelAnimationFrame(restoreRafRef.current);
                    }
                };
            }
        }

        lenisRef.current?.scrollTo(PAGE_TOP, {
            immediate: false,
            duration: 0.45,
        });
    }, [pathname, routeKey]);
}
